import { TranslationKeys } from '@app/i18n/translationKeys';
import { MenuStructure } from '@app/types/routing.types';
import { HeaderProvider } from '@context/header';
import UseRoutingContext from '@context/routing/useRoutingContext';
import useUiContext from '@context/ui/useUiContext';
import { Grid, Layout, MenuProps } from 'antd';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IoArrowBack } from 'react-icons/io5';
import { Outlet } from 'react-router-dom';
import WebPushPermissions from '../features/web-push/notifications-permissions';
import HeaderLayout from './HeaderLayout';
import Sidebar from './Sidebar/Components/Sidebar';

const { Content } = Layout;
const { useBreakpoint } = Grid;

const MasterLayout: React.FC = () => {
  const breakpoints = useBreakpoint();
  const { isSidebarOpen, toggleSidebar } = useUiContext();
  const { xs } = breakpoints;
  const { filteredMenuStructure } = UseRoutingContext();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (xs !== undefined && isSidebarOpen && xs) {
      toggleSidebar();
    }
  }, [xs, isSidebarOpen, toggleSidebar]);

  const menuItems: MenuProps['items'] = useMemo(() => {
    const mapMenuStructure = (menu: MenuStructure): MenuProps['items'] => {
      return menu
        .filter((item) => item.show)
        .map((item) => {
          const hasChildren = Array.isArray(item.children);
          const children = hasChildren ? mapMenuStructure(item.children as MenuStructure) : undefined;

          const hasValidChildren = Array.isArray(children) && children.length > 0;

          if (hasChildren && !hasValidChildren) return null;

          return {
            key: item.key,
            icon: item.icon,
            // item.label is an i18n key (see getMenu.ts); translate for display.
            label: t(item.label as TranslationKeys),
            children: hasValidChildren ? children : undefined,
            matchNested: item.matchNested,
          };
        })
        .filter(Boolean) as MenuProps['items'];
    };

    return mapMenuStructure(filteredMenuStructure);
    // Recompute when the language changes so menu labels follow the active language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredMenuStructure, t, i18n.language]);

  return (
    <HeaderProvider>
      <Layout className="flex h-svh !flex-row p-2 gap-2  overflow-hidden">
        <Sidebar collapseIcon={<IoArrowBack size={'large'} />} menuItems={menuItems as undefined} />

        <Layout className="flex-1 min-w-0 w-full flex flex-col overflow-hidden">
          <div className="absolute w-full top-0 left-0 z-[999]">
            <WebPushPermissions />
          </div>
          <div className="flex flex-col  flex-1 min-w-0 min-h-0 overflow-hidden">
            {/* Header Section */}
            <HeaderLayout />

            {/* Content Section */}
            <Content className="flex-1 min-h-0 overflow-hidden px-4 pt-2 pb-2 flex flex-col gap-4">
              <Outlet />
            </Content>
          </div>
        </Layout>
      </Layout>
    </HeaderProvider>
  );
};

export { MasterLayout };
